import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { MaskLine, Reveal, ease } from "@/components/site/Reveal";
import { Highlights, PurchaseBox } from "@/components/site/ProductBits";
import { QuoteDialog } from "@/components/site/QuoteDialog";

function TiltImage({ src, alt, light }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 16 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div onMouseMove={move} onMouseLeave={() => { mx.set(0); my.set(0); }} style={{ perspective: 1000 }} className={`clip-notch relative aspect-square overflow-hidden ${light ? "bg-white" : "bg-black border border-white/10"}`}>
      {!light && <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(212,230,10,0.16),transparent_60%)]" />}
      <motion.img style={{ rotateX: rx, rotateY: ry }} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease }} src={src} alt={alt} className={`relative h-full w-full object-contain p-8 md:p-14 ${light ? "" : "mix-blend-lighten"}`} />
    </div>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const { products, isLoading } = useProducts();
  const [quote, setQuote] = useState(null);
  const p = products.find((x) => x.slug === slug);

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [slug]);

  if (isLoading) return <div className="min-h-screen bg-obsidian pt-40 text-center font-mono text-zinc-400">Loading…</div>;
  if (!p)
    return (
      <div data-testid="product-not-found" className="min-h-screen bg-obsidian pt-40 text-center">
        <p className="font-display text-5xl font-black uppercase">Product not found</p>
        <Link to="/" className="mt-6 inline-block font-mono text-sm text-volt">Back to home</Link>
      </div>
    );

  const light = p.category === "accessory" && p.image;
  return (
    <main data-testid="product-page" className="min-h-screen bg-obsidian pt-36 md:pt-48">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Link to="/" data-testid="product-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-300 hover:text-volt">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:gap-16">
          {p.image ? <TiltImage src={p.image} alt={`${p.name} ${p.model}`} light={light} /> : <div className="clip-notch aspect-square bg-surface" />}
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-rescue">{p.model}</p>
            <h1 data-testid="product-title" className="mt-3 font-display text-6xl md:text-8xl font-black uppercase leading-[0.85]" style={{ fontWeight: 900 }}>
              {p.name.split(" ").map((w, i) => <MaskLine key={i} delay={0.08 * i}>{w}</MaskLine>)}
            </h1>
            <p className="mt-6 font-display text-2xl md:text-3xl uppercase text-volt">{p.tagline}</p>
            <p className="mt-4 max-w-xl text-zinc-300 leading-relaxed">{p.description}</p>
            <Highlights product={p} className="mt-8" />
            <PurchaseBox product={p} products={products} onQuote={setQuote} size="lg" priceClass="mt-8" />
            {p.specs?.length > 0 && (
              <Reveal className="mt-12">
                <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Specifications</p>
                <dl data-testid="product-specs" className="border-t border-white/10">
                  {p.specs.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 border-b border-white/10 py-3">
                      <dt className="font-mono text-xs uppercase tracking-wider text-zinc-400">{k}</dt>
                      <dd className="font-display text-xl font-bold text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
              </Reveal>
            )}
          </div>
        </div>
        {p.features?.length > 0 && (
          <section className="mt-24 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {p.features.map((f, i) => (
              <Reveal key={f} delay={i * 0.05} className="border border-white/10 bg-surface p-6">
                <span className="font-mono text-xs text-volt">0{i + 1}</span>
                <p className="mt-6 font-display text-3xl font-extrabold uppercase leading-none">{f}</p>
              </Reveal>
            ))}
          </section>
        )}
        {p.action_video ? (
          <Reveal className="mt-24 mb-24">
            <div data-testid="product-action-video-section" className="grid items-center gap-10 md:grid-cols-2">
              <div className="clip-notch relative mx-auto aspect-[9/16] w-full max-w-sm overflow-hidden bg-black">
                <video data-testid="product-action-video" poster={p.action_image || undefined} autoPlay muted loop playsInline controls preload="metadata" className="absolute inset-0 h-full w-full object-cover">
                  <source src={p.action_video.replace(/\.mp4$/, ".webm")} type="video/webm" />
                  <source src={p.action_video} type="video/mp4" />
                </video>
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-volt">On the job</p>
                <p className="mt-4 font-display text-5xl md:text-7xl font-black uppercase leading-[0.9]" style={{ fontWeight: 900 }}>Watch it <span className="text-outline-volt">break.</span></p>
                <p className="mt-5 max-w-md text-zinc-300">The {p.name} working through concrete rubble in the field.</p>
              </div>
            </div>
          </Reveal>
        ) : p.action_image ? (
          <Reveal className="mt-24 mb-24">
            <div className="clip-notch relative aspect-[16/9] overflow-hidden">
              <img src={p.action_image} alt={`${p.name} in use`} className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </Reveal>
        ) : null}
        {!p.action_image && !p.action_video && <div className="mb-24" />}
      </div>
      <QuoteDialog product={quote} onClose={() => setQuote(null)} />
    </main>
  );
}
